// src/components/ClientManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import logo from '../images/247Logo.png';
import '../styles/ClientManager.css';
import '../styles/fleet-theme.css';
import { apiFetchJSON } from '../api/apiClient';
import { FieldError, getConflictField, invalidInputStyle } from './fieldErrors';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';

// Badge Management is hidden from the UI. The code, API calls and data are
// left in place - set this to true to bring the section back.
const SHOW_BADGE_MANAGEMENT = false;

function ClientManager({ user }) {
    const [currentUsername, setCurrentUsername] = useState(null);
    const [clients, setClients] = useState([]);
    // client_id -> array of sites (each with .devices, mixing Peplink +
    // Starlink) - replaces the old "assigned routers" concept now that
    // devices reach a client via site linkage, not direct assignment.
    const [sitesByClient, setSitesByClient] = useState({});
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [sendingReset, setSendingReset] = useState(false);
    const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';
    
    // Form states
    const [newClient, setNewClient] = useState({
        client_name: '',
        contact_email: ''
    });
    
    const [newUser, setNewUser] = useState({
        client_id: '',
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'viewer'
    });
    
    const [editingClient, setEditingClient] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [resettingPassword, setResettingPassword] = useState(null);
    
    // UI states
    const [showNewUserForm, setShowNewUserForm] = useState(false);
    const [showVerificationInfo, setShowVerificationInfo] = useState(null);
    const [expandedClient, setExpandedClient] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    // Errors shown under a form field, e.g. { email: 'This email is already used...' }
    const [clientFieldErrors, setClientFieldErrors] = useState({}); // ADD CLIENT bar
    const [userFieldErrors, setUserFieldErrors] = useState({});     // add/edit user modals

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showMobileAddClient, setShowMobileAddClient] = useState(false);

    // Organizations list - shared with Badge Management's org dropdown,
    // so this stays even though the router-assign feature that used to
    // also read it is gone.
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);

    const [cachedOrganizations, setCachedOrganizations] = useState(null);
    const [cachedBadges, setCachedBadges] = useState(null);

    // Badge management
    const [clientBadges, setClientBadges] = useState({});
    const [showBadgeModal, setShowBadgeModal] = useState(false);
    const [showUnbadgedOrgs, setShowUnbadgedOrgs] = useState(false);
    const [badgeFormData, setBadgeFormData] = useState({
        organization_id: '',
        badge_name: '',
        badge_color: '#3b82f6',
        badge_font_color: '#ffffff'
    });
    const [editingBadgeOrg, setEditingBadgeOrg] = useState(null);
    const [loadingBadges, setLoadingBadges] = useState(false);

    useEffect(() => {
        // Prefer the user passed from App (already fetched from /api/auth/me).
        // Only fall back to fetching if the prop wasn't provided.
        if (user) {
            setCurrentUsername(user.username || null);
            return;
        }
        const loadMe = async () => {
            try {
                const me = await apiFetchJSON('/api/auth/me');
                setCurrentUsername(me.username || null);
            } catch (e) {
                // Non-fatal - self-protection checks (e.g. "can't delete your
                // own account") just won't match anything, which is safe.
            }
        };
        loadMe();
    }, [user]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Group /api/v2/admin/sites by the client each site is linked to.
    const groupSitesByClient = (sites) => {
        const byClient = {};
        (sites || []).forEach((site) => {
            if (!site.client_id) return;
            if (!byClient[site.client_id]) byClient[site.client_id] = [];
            byClient[site.client_id].push(site);
        });
        return byClient;
    };

    // Every device on the client's linked site(s), de-duplicated by
    // device_type + device_key. This is the single source for both the
    // DEVICES counter and the device list in the expanded row.
    const getClientDevices = (clientId) => {
        const seen = new Set();
        const devices = [];
        (sitesByClient[clientId] || []).forEach((site) => {
            (site.devices || []).forEach((d) => {
                const key = d.device_type + ':' + d.device_key;
                if (seen.has(key)) return;
                seen.add(key);
                devices.push({ ...d, site_name: site.site_name });
            });
        });
        return devices;
    };

    const getClientDeviceCount = (clientId) => getClientDevices(clientId).length;

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [clientsRes, sitesRes] = await Promise.all([
                    apiFetchJSON('/api/admin/client-management/clients'),
                    apiFetchJSON('/api/v2/admin/sites')
                ]);
                
                const clients = clientsRes.clients || [];
                setClients(clients);

                // Group sites by the client they're linked to, so each
                // client's card can show every Peplink + Starlink device
                // on their site(s) - devices reach a client via site
                // linkage now, not direct per-router assignment.
                setSitesByClient(groupSitesByClient(sitesRes.sites));
                
                setLastUpdated(formatDateTime(new Date()));
                
                // Fetch organizations and badges in parallel and cache them
                await Promise.all([
                    fetchOrganizationsFromPeplink(),
                    fetchBadges()
                ]);
                
            } catch (err) {
                showMessage(err.message, 'error');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const parseUTCTimeString = (timestamp) => {
        if (!timestamp) return null;
        
        // If it's already a Date object or number, return it
        if (timestamp instanceof Date) return timestamp;
        if (typeof timestamp === 'number') return new Date(timestamp * 1000);
        
        // If it's a string with Z or timezone, parse normally
        if (timestamp.includes('Z') || timestamp.includes('+')) {
          return new Date(timestamp);
        }
        
        // Parse UTC time string (assuming format: "YYYY-MM-DD HH:MM:SS")
        try {
          // Replace space with 'T' and add 'Z' to indicate UTC
          const isoString = timestamp.replace(' ', 'T') + 'Z';
          return new Date(isoString);
        } catch (error) {
          console.error('Error parsing time:', timestamp, error);
          return null;
        }
    };

    const formatLoginTime = (alertTime) => {
        const date = parseUTCTimeString(alertTime);
        if (!date || isNaN(date.getTime())) return 'N/A';
        
        return new Intl.DateTimeFormat('en-GB', {
          //timeZone: 'Europe/Paris',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).format(date);
    };

    const getDeviceBadge = (router) => {

      const badgeConfig = clientBadges[router.organization_id];
      if (badgeConfig) {
        return {
          label: badgeConfig.badge_name,
          backgroundColor: badgeConfig.badge_color,
          color: badgeConfig.badge_font_color,
          icon: 'fa-building'
        };
      }
      
      switch(router.device_type) {
        case 'fully_managed':
          return {
            label: 'FM',
            backgroundColor: 'var(--color-fm)', 
            color: 'var(--color-background)',
            icon: 'fa-network-wired'
          };
        case 'wan_managed':
          return {
            label: 'WM',
            backgroundColor: 'var(--color-wm)', 
            color: 'var(--color-background)',
            icon: 'fa-wifi'
          };
        case 'data_center':
          return {
            label: 'DC',
            backgroundColor: 'var(--color-dc)', 
            color: 'var(--color-background)',
            icon: 'fa-server'
          };
        default:
          return {
            label: '?',
            backgroundColor: 'var(--color-muted)', 
            color: 'var(--color-background)',
            icon: 'fa-question'
          };
      }
    };

    const getClientStatus = (client) => {
        if (!client.is_verified) return 'pending';
        if (client.is_active) return 'active';
        return 'inactive';
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'Unknown';
        
        let date;
        
        if (typeof timestamp === 'number') {
            date = new Date(timestamp * 1000);
        } else {
            date = new Date(timestamp);
        }
        
        if (isNaN(date.getTime())) return 'Invalid date';
        
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return `${day} ${month} ${year} ${hours}:${minutes}`;
    };

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const fetchClients = async () => {
        try {
            setLoading(true);
            // Refresh sites alongside clients so the DEVICES counters stay
            // in sync (device ownership comes from Site Manager).
            const [response, sitesRes] = await Promise.all([
                apiFetchJSON('/api/admin/client-management/clients'),
                apiFetchJSON('/api/v2/admin/sites')
            ]);
            setClients(response.clients || []);
            setSitesByClient(groupSitesByClient(sitesRes.sites));
            setLastUpdated(formatDateTime(new Date()));
        } catch (error) {
            console.error('Error fetching clients:', error);
            showMessage('Failed to load clients: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchBadges = async () => {
        try {
            const response = await apiFetchJSON('/api/client-badges');
            if (response) {
                setClientBadges(response);
                setCachedBadges(response); // Cache the badges data
            }
        } catch (error) {
            console.error('Error fetching badges:', error);
        }
    };

    const fetchOrganizationsForBadges = async () => {
        setLoadingBadges(true);
        try {
            let orgs = cachedOrganizations;
            let badges = cachedBadges;
            
            // If organizations not cached yet, fetch them
            if (!orgs) {
                orgs = await fetchOrganizationsFromPeplink();
            }
            
            // If badges not cached yet, fetch them
            if (!badges) {
                const response = await apiFetchJSON('/api/client-badges');
                badges = response || {};
                setCachedBadges(badges);
            }
            
            const existingBadgeOrgIds = new Set(Object.keys(badges || {}));
            
            const formattedOrgs = orgs.map(org => ({
                ...org,
                hasBadge: existingBadgeOrgIds.has(org.id)
            }));
            
            formattedOrgs.sort((a, b) => a.name.localeCompare(b.name));
            setOrganizations(formattedOrgs);
        } catch (error) {
            console.error('Error fetching organizations for badges:', error);
            // Fallback: try to fetch fresh data
            try {
                const badgesResponse = await apiFetchJSON('/api/client-badges');
                const existingBadgeOrgIds = new Set(Object.keys(badgesResponse || {}));
                
                const response = await apiFetchJSON('/api/devices/manage/list');
                const allDevices = [
                    ...(response.configured_devices || []),
                    ...(response.orphaned_devices || [])
                ];
                
                const orgMap = new Map();
                allDevices.forEach(device => {
                    const orgId = device.organization_id;
                    if (orgId && orgId !== 'Unknown' && !orgMap.has(orgId)) {
                        orgMap.set(orgId, {
                            id: orgId,
                            name: orgId,
                            display_name: orgId,
                            hasBadge: existingBadgeOrgIds.has(orgId)
                        });
                    }
                });
                
                const orgs = Array.from(orgMap.values());
                orgs.sort((a, b) => a.display_name.localeCompare(b.display_name));
                setOrganizations(orgs);
            } catch (fallbackError) {
                console.error('Error fetching organizations from devices:', fallbackError);
                setOrganizations([]);
            }
        } finally {
            setLoadingBadges(false);
        }
    };

    useEffect(() => {
        if (showUnbadgedOrgs && organizations.length === 0) {
            fetchOrganizationsForBadges();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUnbadgedOrgs]);

    const handleSaveBadge = async () => {
        if (!badgeFormData.organization_id) {
            showMessage('Please select an organization', 'error');
            return;
        }
        
        if (!badgeFormData.badge_name) {
            showMessage('Please enter a badge name', 'error');
            return;
        }

        if (badgeFormData.badge_name.length > 6) {
            showMessage('Badge name must be 6 characters or less', 'error');
            return;
        }
        
        setLoadingBadges(true);
        try {
            const result = await apiFetchJSON('/api/admin/client-badges', {
                method: 'POST',
                body: JSON.stringify({
                    organization_id: badgeFormData.organization_id,
                    badge_name: badgeFormData.badge_name,
                    badge_color: badgeFormData.badge_color,
                    badge_font_color: badgeFormData.badge_font_color,
                })
            });
            
            if (result) {
                setShowBadgeModal(false);
                await fetchBadges();
                showMessage('Badge configuration saved successfully!', 'success');
                // Reset form
                setBadgeFormData({
                    organization_id: '',
                    badge_name: '',
                    badge_color: '#3b82f6',
                    badge_font_color: '#ffffff',
                });
                setEditingBadgeOrg(null);
            }
        } catch (error) {
            showMessage(error.message || 'Failed to save badge configuration', 'error');
        } finally {
            setLoadingBadges(false);
        }
    };

    const handleEditBadge = (orgId) => {
        const badge = clientBadges[orgId];
        if (badge) {
            const badgeName = badge.badge_name || '';
            setBadgeFormData({
                organization_id: badge.organization_id || orgId,
                badge_name: badgeName.length > 6 ? badgeName.slice(0, 6) : badgeName,
                badge_color: badge.badge_color || '#3b82f6',
                badge_font_color: badge.badge_font_color || '#ffffff'
            });
            setEditingBadgeOrg(orgId);
            setShowBadgeModal(true);
        }
    };

    const handleDeleteBadge = async (orgId, badgeName) => {
        if (!window.confirm(`Are you sure you want to delete the badge "${badgeName}" for this organization?`)) {
            return;
        }
        
        setLoadingBadges(true);
        try {
            const result = await apiFetchJSON(`/api/admin/client-badges/${orgId}`, {
                method: 'DELETE'
            });
            
            if (result && result.success) {
                await fetchBadges();
                showMessage('Badge configuration deleted successfully!', 'success');
            }
        } catch (error) {
            showMessage(error.message || 'Failed to delete badge configuration', 'error');
        } finally {
            setLoadingBadges(false);
        }
    };

    const handleOpenBadgeModal = () => {
        setBadgeFormData({
            organization_id: '',
            badge_name: '',
            badge_color: '#3b82f6',
            badge_font_color: '#ffffff'
        });
        setEditingBadgeOrg(null);
        setShowBadgeModal(true);
        fetchOrganizationsForBadges();
    };

    const colorPalettes = [
        { bg: '#f50550', font: '#ffffff', name: 'Pink' },
        { bg: '#e11d48', font: '#ffffff', name: 'Rose' },
        { bg: '#ea580c', font: '#ffffff', name: 'Orange' },
        { bg: '#b45309', font: '#ffffff', name: 'Amber Dark' },
        { bg: '#facc15', font: '#1c1917', name: 'Yellow' },
        { bg: '#65a30d', font: '#ffffff', name: 'Green' },
        { bg: '#0f9b8e', font: '#ffffff', name: 'Teal' },
        { bg: '#0891b2', font: '#ffffff', name: 'Cyan' },
        { bg: '#2563eb', font: '#ffffff', name: 'Blue' },
        { bg: '#6d28d9', font: '#ffffff', name: 'Purple' },
        { bg: '#64748b', font: '#ffffff', name: 'Slate' },
        { bg: '#1f2937', font: '#ffffff', name: 'Dark' }
    ];

    // WCAG 2.1 contrast ratio - standard relative-luminance formula.
    // AA for normal-size text requires >= 4.5:1; that's the threshold
    // used everywhere below (the "Save" button, the PASS/FAIL chip in
    // the badge table, and the "Apply Fix" suggestion).
    const CONTRAST_AA_THRESHOLD = 4.5;

    const hexToRgb = (hex) => {
        const clean = (hex || '').replace('#', '');
        const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
        const num = parseInt(full, 16);
        if (isNaN(num) || full.length !== 6) return null;
        return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
    };

    const relativeLuminance = ({ r, g, b }) => {
        const [rs, gs, bs] = [r, g, b].map((c) => {
            const s = c / 255;
            return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
        });
        return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };

    const contrastRatio = (hexA, hexB) => {
        const a = hexToRgb(hexA);
        const b = hexToRgb(hexB);
        if (!a || !b) return null;
        const lA = relativeLuminance(a);
        const lB = relativeLuminance(b);
        const lighter = Math.max(lA, lB);
        const darker = Math.min(lA, lB);
        return (lighter + 0.05) / (darker + 0.05);
    };

    // Whichever of pure white / near-black gives the higher ratio against
    // the given background - used by the "Apply Fix" button rather than
    // guessing a single fixed replacement color.
    const bestTextColorFor = (bgHex) => {
        const white = contrastRatio(bgHex, '#FFFFFF') || 0;
        const black = contrastRatio(bgHex, '#0B1618') || 0;
        return white >= black ? '#FFFFFF' : '#0B1618';
    };

    // ALL CAPS, letters/digits/underscore only, matching the mockup's
    // live-validated badge name field.
    const BADGE_NAME_PATTERN = /^[A-Z0-9_]*$/;
    const sanitizeBadgeName = (value) => value.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 6);

    const fetchOrganizationsFromPeplink = async () => {
        setLoadingOrgs(true);
        try {
            const response = await apiFetchJSON('/api/peplink/organizations');
            const orgs = response.organizations || [];
            
            // Format organizations with display name
            const formattedOrgs = orgs.map(org => ({
                id: org.id,
                name: org.name,
                display_name: `${org.name} (${org.id})`,
                org_code: org.org_code,
                status: org.status
            }));
            
            // Sort alphabetically by name
            formattedOrgs.sort((a, b) => a.name.localeCompare(b.name));
            
            setOrganizations(formattedOrgs);
            setCachedOrganizations(formattedOrgs); // Cache the organizations data
            
            return formattedOrgs; // Return for any immediate use
        } catch (error) {
            console.error('Error fetching organizations from Peplink:', error);
            // Fallback to device list if Peplink API fails
            const fallbackOrgs = await fetchOrganizationsFromDevices();
            setCachedOrganizations(fallbackOrgs);
            return fallbackOrgs;
        } finally {
            setLoadingOrgs(false);
        }
    };

    const fetchOrganizationsFromDevices = async () => {
        try {
            // Get unique organizations from the device list
            const response = await apiFetchJSON('/api/devices/manage/list');
            const allDevices = [
                ...(response.configured_devices || []),
                ...(response.orphaned_devices || [])
            ];
            
            // Extract unique organizations
            const orgMap = new Map();
            allDevices.forEach(device => {
                const orgId = device.organization_id;
                if (orgId && orgId !== 'Unknown' && !orgMap.has(orgId)) {
                    orgMap.set(orgId, {
                        id: orgId,
                        name: orgId,
                        display_name: orgId
                    });
                }
            });
            
            const orgs = Array.from(orgMap.values());
            orgs.sort((a, b) => a.display_name.localeCompare(b.display_name));
            setOrganizations(orgs);
            return orgs;
        } catch (error) {
            console.error('Error fetching organizations from devices:', error);
            setOrganizations([]);
            return [];
        }
    };

    // Start every add/edit user form without leftover field errors.
    useEffect(() => {
        setUserFieldErrors({});
    }, [showNewUserForm, editingUser?.user_id]);

    const clearUserFieldError = (field) =>
        setUserFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    const clearClientFieldError = (field) =>
        setClientFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

    const handleAddClient = async (e) => {
        e?.preventDefault();
        
        if (!newClient.contact_email || !newClient.client_name) {
            showMessage('Client name and contact email are required', 'error');
            return false;
        }
        
        try {
            setLoading(true);
            await apiFetchJSON('/api/admin/client-management/add-client', {
                method: "POST",
                body: JSON.stringify(newClient)
            });

            showMessage(
                `Client "${newClient.client_name}" created! Verification email sent to ${newClient.contact_email}`,
                'success'
            );
            
            // Show info modal about email verification
            setShowVerificationInfo({
                email: newClient.contact_email,
                clientName: newClient.client_name
            });
            
            // Reset form
            setNewClient({
                client_name: '',
                contact_email: ''
            });
            
            fetchClients();
            return true;
            
        } catch (error) {
            const errorMessage = error.message || 'Failed to add client';
            const field = getConflictField(error);
            if (field === 'email') {
                setClientFieldErrors({ email: errorMessage });
            } else if (field === 'username') {
                // The client's first admin is named "<client name>.admin", so a
                // username clash means this client name needs to change.
                setClientFieldErrors({
                    client_name: `${errorMessage}. Please use a different client name.`
                });
            } else {
                showMessage(errorMessage, 'error');
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleEditClient = async () => {
        if (!editingClient) return;
        
        if (!editingClient.contact_email || !editingClient.contact_email.trim()) {
            showMessage('Contact email is required', 'error');
            return;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editingClient.contact_email)) {
            showMessage('Please enter a valid email address', 'error');
            return;
        }
        
        try {
            await apiFetchJSON('/api/admin/client-management/clients/' + editingClient.client_id, {
                method: "PUT",
                body: JSON.stringify({
                    client_name: editingClient.client_name,
                    contact_email: editingClient.contact_email
                })
            });
            
            showMessage('Client updated successfully!');
            setEditingClient(null);
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to update client', 'error');
        }
    };

    const handleToggleClientStatus = async (clientId, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this client?`)) {
            return;
        }
        
        try {
            await apiFetchJSON('/api/admin/client-management/clients/' + clientId + '/status', {
                method: "PUT",
                body: JSON.stringify({
                    is_active: !currentStatus
                })
            });

            showMessage(`Client ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to update client status', 'error');
        }
    };

    const handleToggleClientSkipStarlink = async (clientId, skipStarlink) => {
        if (!window.confirm(`Are you sure you want to ${skipStarlink ? 'show Starlink' : 'hide Starlink'} for this client?`)) {
            return;
        }
        
        try {
            await apiFetchJSON('/api/admin/client-management/clients/' + clientId + '/skip-starlink', {
                method: "PUT",
                body: JSON.stringify({
                    skip_starlink: !skipStarlink
                })
            });

            showMessage(`Client ${skipStarlink ? 'showing Starlink' : 'hiding Starlink'} info successfully!`);
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to update client skip starlink', 'error');
        }
    };

    const handleDeleteClient = async (clientId, clientName) => {
        if (!window.confirm(`Are you sure you want to delete client "${clientName}"? This will also delete all associated users.`)) {
            return;
        }
        
        try {
            await apiFetchJSON('/api/admin/client-management/clients/' + clientId, {
                method: "DELETE"
            });

            showMessage(`Client "${clientName}" deleted successfully!`);
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to delete client', 'error');
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!newUser.client_id || !newUser.username || !newUser.email) {
            showMessage('Client, username, and email are required', 'error');
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newUser.email)) {
            showMessage('Please enter a valid email address', 'error');
            return;
        }
        
        try {
            setLoading(true);
            
            await apiFetchJSON('/api/admin/client-management/add-user', {
                method: "POST",
                body: JSON.stringify(newUser)
            });

            showMessage(
                `User "${newUser.username}" created! Setup email sent to ${newUser.email}`,
                'success'
            );
            
            // Reset form
            setNewUser({
                client_id: '',
                username: '',
                password: '',
                full_name: '',
                email: '',
                role: 'viewer'
            });
            
            setShowNewUserForm(false);
            fetchClients();
            
        } catch (error) {
            const errorMessage = error.message || 'Failed to add user';
            const field = getConflictField(error);
            if (field) {
                // Show it under the field - the banner is hidden behind the form
                setUserFieldErrors({ [field]: errorMessage });
            } else {
                showMessage(errorMessage, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = async () => {
        if (!editingUser) return;
        
        try {
            await apiFetchJSON(`/api/admin/client-management/clients/${editingUser.client_id}/users/${editingUser.user_id}`, {
                method: "PUT",
                body: JSON.stringify({
                    full_name: editingUser.full_name,
                    email: editingUser.email,
                    role: editingUser.role,
                    username: editingUser.username
                })
            });
            
            showMessage('User updated successfully!');
            setEditingUser(null);
            fetchClients();
        } catch (error) {
            const errorMessage = error.message || 'Failed to update user';
            if (getConflictField(error) === 'email') {
                setUserFieldErrors({ email: errorMessage });
            } else {
                showMessage(errorMessage, 'error');
            }
        }
    };

    const handleDeleteUser = async (clientId, userId, username) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
            return;
        }
        
        try {
            await apiFetchJSON(`/api/admin/client-management/clients/${clientId}/users/${userId}`, {
                method: "DELETE"
            });

            showMessage('User deleted successfully!');
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to delete user', 'error');
        }
    };

    const handleSendResetEmail = async () => {
        if (!resettingPassword) return;
        
        setSendingReset(true);
        try {
            const response = await apiFetchJSON('/api/admin/client-management/send-reset-email', {
                method: "POST",
                body: JSON.stringify({
                    user_id: resettingPassword.user_id,
                    client_id: resettingPassword.client_id
                })
            });
            
            if (response.success) {
                showMessage(`Password reset email sent to ${resettingPassword.email}`, 'success');
                setResettingPassword(null);
            } else {
                showMessage(response.error || 'Failed to send password reset email', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Failed to send password reset email', 'error');
        } finally {
            setSendingReset(false);
        }
    };

    const handleToggleUserStatus = async (clientId, userId, username, currentStatus) => {
        const newStatus = !currentStatus;
        const action = newStatus ? 'activate' : 'deactivate';
        
        if (!window.confirm(`Are you sure you want to ${action} user "${username}"?`)) {
            return;
        }
        
        try {
            await apiFetchJSON(`/api/admin/client-management/clients/${clientId}/users/${userId}/status`, {
                method: "PUT",
                body: JSON.stringify({
                    is_active: newStatus
                })
            });

            showMessage(`User "${username}" ${action}d successfully!`);
            fetchClients();
        } catch (error) {
            showMessage(error.message || `Failed to ${action} user`, 'error');
        }
    };

    // Per-account push switch. Only operator/admin roles are ever sent push
    // alerts by the server, so viewers get a disabled button.
    const handleToggleUserPush = async (clientId, u) => {
        const enable = !u.push_enabled;
        if (!enable && !window.confirm(
            `Turn off push notifications for "${u.username}"? All of their devices will stop receiving alerts until turned back on.`
        )) return;
        try {
            await apiFetchJSON(`/api/admin/client-management/clients/${clientId}/users/${u.user_id}/push`, {
                method: 'PUT',
                body: JSON.stringify({ push_enabled: enable })
            });
            showMessage(`Push notifications ${enable ? 'enabled' : 'disabled'} for "${u.username}"`);
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to update push notifications', 'error');
        }
    };

    // Lost phone: turn off the user's authenticator app (back to email codes)
    const handleReset2fa = async (clientId, u) => {
        if (!window.confirm(
            `Reset 2FA for "${u.username}"? Their authenticator app and backup codes will stop working and login codes will go to their email instead.`
        )) return;
        try {
            await apiFetchJSON(`/api/admin/client-management/clients/${clientId}/users/${u.user_id}/reset-2fa`, {
                method: 'POST'
            });
            showMessage(`2FA reset for "${u.username}"`);
            fetchClients();
        } catch (error) {
            showMessage(error.message || 'Failed to reset 2FA', 'error');
        }
    };

    const pushTitle = (u) =>
        u.role === 'viewer'
            ? 'Viewers do not receive push alerts'
            : (u.push_enabled ? 'Push notifications are on - click to turn off' : 'Push notifications are off - click to turn on');

    const toggleClientExpansion = (clientId) => {
        setExpandedClient(expandedClient === clientId ? null : clientId);
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you want to logout?')) {
            try {
                await apiFetchJSON('/api/auth/logout', {
                    method: "POST",
                });
                window.location.href = '/login';
            } catch (error) {
                console.error('Logout error:', error);
            }
        }
    };

    const handleBackToDashboard = () => {
        window.location.href = '/';
    };

    const getDefaultUsernamePrefix = (clientName) => {
        if (!clientName) return '';
        
        const prefix = clientName.toLowerCase()
            .replace(/\s+/g, '.')
            .replace(/[^a-z0-9.-]/g, '');
        
        return `${prefix}.`;
    };

    const handleClientSelectForUser = (clientId) => {
        const selectedClient = clients.find(c => c.client_id === clientId);
        if (selectedClient) {
            const prefix = getDefaultUsernamePrefix(selectedClient.client_name);
            setNewUser(prev => ({
                ...prev,
                client_id: clientId,
                username: prefix,
                usernameSuffix: ''
            }));
        } else {
            setNewUser(prev => ({
                ...prev,
                client_id: clientId,
                username: '',
                usernameSuffix: ''
            }));
        }
    };

    // ---- Guided tour ("?" in FleetHeader) ---------------------------------
    // Example client: prefer an active one that already has users, so the
    // expanded detail step has something to show.
    const tourClient =
        clients.find((c) => c.is_verified && c.is_active && (c.users || []).length > 0) ||
        clients.find((c) => (c.users || []).length > 0) ||
        clients[0] || null;
    const tourSnapshotRef = useRef(null);
    usePageTour('client-manager', {
        isMobile,
        clients,
        tourClient,
        tourClientStatus: tourClient ? getClientStatus(tourClient) : null,
        tourClientDeviceCount: tourClient
            ? getClientDeviceCount(tourClient.client_id)
            : 0,
        badgeCount: Object.keys(clientBadges).length,
        showBadges: SHOW_BADGE_MANAGEMENT,
        orgCount: organizations.length,
        openMobileAddPanel: () => setShowMobileAddClient(true),
        closeMobileAddPanel: () => setShowMobileAddClient(false),
        expandTourClient: () => tourClient && setExpandedClient(tourClient.client_id),
        onTourStart: () => {
            tourSnapshotRef.current = { expandedClient, showMobileAddClient };
        },
        onTourEnd: () => {
            if (!tourSnapshotRef.current) return;
            setExpandedClient(tourSnapshotRef.current.expandedClient);
            setShowMobileAddClient(tourSnapshotRef.current.showMobileAddClient);
        },
    }, { ready: !loading });

    if (loading) return (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading client data…</p>
          </div>
        </div>
    );

    return (
        <div className="App cm-client-manager">
            <FleetHeader 
                title="Client Manager" 
                user={user} 
                onLogout={handleLogout} 
            />
            
            {/* Main Container */}
            <div className="cm-dashboard-container">
                {message.text && (
                    <div className={`ft-toolbar-${message.type === 'error' ? 'error' : 'success'}`} role="alert">
                        <i className={`fas fa-${message.type === 'error' ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
                        {message.text}
                        <button type="button" className="btn-close" onClick={() => setMessage({ text: '', type: '' })}></button>
                    </div>
                )}

                {/* Add Client Form */}
                <div className="cm-add-client-bar" data-tour="cm-add">
                    {isMobile && (
                        <button
                            className="cm-mobile-toggle"
                            data-tour="cm-add-toggle"
                            onClick={() => setShowMobileAddClient((s) => !s)}
                        >
                            {showMobileAddClient ? 'HIDE' : '+ ADD CLIENT'}
                        </button>
                    )}
                    {(!isMobile || showMobileAddClient) && (
                        <div className="cm-add-form-grid" data-tour="cm-add-form">
                            <div className="cm-field">
                                <label htmlFor="clientName">CLIENT NAME *</label>
                                <input
                                    id="clientName"
                                    type="text"
                                    placeholder="e.g. Acme Corporation"
                                    value={newClient.client_name}
                                    onChange={(e) => {
                                        clearClientFieldError('client_name');
                                        setNewClient({...newClient, client_name: e.target.value});
                                    }}
                                    className="cm-input"
                                    style={clientFieldErrors.client_name ? invalidInputStyle : undefined}
                                    aria-invalid={!!clientFieldErrors.client_name}
                                    required
                                />
                                <FieldError message={clientFieldErrors.client_name} />
                            </div>
                            <div className="cm-field">
                                <label htmlFor="contactEmail">CONTACT EMAIL *</label>
                                <input
                                    id="contactEmail"
                                    type="email"
                                    placeholder="client@example.com"
                                    value={newClient.contact_email}
                                    onChange={(e) => {
                                        clearClientFieldError('email');
                                        setNewClient({...newClient, contact_email: e.target.value});
                                    }}
                                    className="cm-input"
                                    style={clientFieldErrors.email ? invalidInputStyle : undefined}
                                    aria-invalid={!!clientFieldErrors.email}
                                    required
                                />
                                <FieldError message={clientFieldErrors.email} />
                            </div>
                            <button 
                                className="cm-add-btn"
                                onClick={async (e) => {
                                    const ok = await handleAddClient(e);
                                    if (ok) setShowMobileAddClient(false);
                                }}
                            >
                                + ADD CLIENT
                            </button>
                        </div>
                    )}
                </div>

                {/* Clients List */}
                <div className="cm-panel">
                    <div className="cm-tabs-row">
                        <span className="cm-tab cm-active">CLIENTS {clients.length}</span>
                    </div>
                    <div className="cm-panel-body">
                        {clients.length === 0 ? (
                            <div className="cm-text-center cm-py-4">
                                <div className="cm-empty-state">
                                    <h5>No clients found</h5>
                                    <p className="cm-text-muted">Add your first client to get started</p>
                                </div>
                            </div>
                        ) : isMobile ? (
                            <div className="cm-client-mobile-list">
                                            {clients.map(client => (
                                                <div key={client.client_id} className="cm-client-mobile-card" data-tour={tourClient && client.client_id === tourClient.client_id ? 'cm-client' : undefined}>
                                                    <div className="cmc-header">
                                                        <div className="cmc-name">
                                                            {client.client_name}
                                                        </div>

                                                        <div className="cmc-user-status">
                                                            <span className={`cmc-user-role cmc-user-role-${getClientStatus(client) === 'pending' ? 'viewer' : getClientStatus(client) === 'active' ? 'operator' : 'admin'}`}>
                                                                {getClientStatus(client) === 'pending' ? 'PENDING' : getClientStatus(client) === 'active' ? 'ACTIVE' : 'INACTIVE'}
                                                            </span>
                                                        </div>

                                                    </div>

                                                    <div className="cmc-subrow">
                                                        <span className="cmc-email">{client.contact_email || '-'} · ID {client.client_id}</span>
                                                    </div>

                                                    <div className="cmc-stats">
                                                        <div className="cmc-stat">
                                                            {client.user_count || 0} {client.user_count > 1 ? 'USERS' : 'USER'}
                                                        </div>
                                                        <div className="cmc-stat">
                                                            {getClientDeviceCount(client.client_id)} {getClientDeviceCount(client.client_id) === 1 ? 'DEVICE' : 'DEVICES'}
                                                        </div>
                                                        <div className="cmc-stat cmc-stat-muted" title="Client account created">
                                                            {new Date(client.created_at).toLocaleDateString()}
                                                        </div>
                                                    </div>

                                                    <div className="cmc-actions" data-tour={tourClient && client.client_id === tourClient.client_id ? 'cm-client-actions' : undefined}>
                                                        <button
                                                            className="cmc-action-label"
                                                            onClick={() => {
                                                                setEditingClient({
                                                                    client_id: client.client_id,
                                                                    client_name: client.client_name,
                                                                    contact_email: client.contact_email || ''
                                                                });
                                                            }}
                                                            title="Edit Client"
                                                        >
                                                            EDIT
                                                        </button>
                                                        <button
                                                            className="cmc-action-label"
                                                            onClick={() => {
                                                                setNewUser(prev => ({
                                                                    ...prev,
                                                                    client_id: client.client_id,
                                                                    full_name: `User for ${client.client_name}`
                                                                }));
                                                                setShowNewUserForm(true);
                                                            }}
                                                            title="Add User"
                                                        >
                                                            + USER
                                                        </button>
                                                        <button
                                                            className="cmc-action-label"
                                                            onClick={() => {
                                                                if (!client.is_verified) {
                                                                    showMessage('Cannot change status until client verifies email', 'warning');
                                                                    return;
                                                                }
                                                                handleToggleClientStatus(client.client_id, client.is_active);
                                                            }}
                                                            title={!client.is_verified ? 'Must verify email first' : (client.is_active ? 'Deactivate' : 'Activate')}
                                                            disabled={!client.is_verified}
                                                        >
                                                            {!client.is_verified ? 'PENDING' : (client.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                                        </button>
                                                        <button
                                                            className="cmc-action-label"
                                                            onClick={() => handleToggleClientSkipStarlink(client.client_id, client.skip_starlink)}
                                                            title={client.skip_starlink ? 'Show Starlink' : 'Hide Starlink'}
                                                        >
                                                            {client.skip_starlink ? 'SHOW SL' : 'HIDE SL'}
                                                        </button>
                                                        <button
                                                            className="cmc-action-label cmc-action-danger"
                                                            onClick={() => handleDeleteClient(client.client_id, client.client_name)}
                                                            title="Delete Client"
                                                        >
                                                            DELETE
                                                        </button>
                                                        <button
                                                            className="cmc-action-label cmc-action-muted"
                                                            onClick={() => toggleClientExpansion(client.client_id)}
                                                            title={expandedClient === client.client_id ? 'Collapse' : 'Expand'}
                                                        >
                                                            {expandedClient === client.client_id ? 'COLLAPSE' : 'EXPAND'}
                                                        </button>
                                                    </div>

                                                    {expandedClient === client.client_id && (
                                                        <div className="cmc-expanded" data-tour="cm-client-detail">
                                                            {client.users && client.users.length > 0 ? (
                                                                <div className="cmc-expanded-section">
                                                                    <div className="cmc-expanded-title"> Users ({client.users.length})
                                                                    </div>
                                                                    <div className="cmc-user-list">
                                                                        {client.users.map(u => (
                                                                            <div key={u.user_id} className="cmc-user-row">
                                                                                <div className="cmc-user-info">
                                                                                    <div className="cmc-user-name">
                                                                                        {u.username || u.full_name} 
                                                                                        <span className={`cmc-user-role cmc-user-role-user cmc-user-role-${u.role}`}>
                                                                                            {u.role === 'admin' ? 'Admin' : u.role === 'operator' ? 'Operator' : 'Viewer'}
                                                                                        </span> 
                                                                                    </div>
                                                                                    <div className="cmc-user-meta">
                                                                                        <span className={`cmc-user-role cmc-user-role-${!u.is_verified ? 'viewer' : u.is_active ? 'operator' : 'admin'}`}>
                                                                                            {!u.is_verified ? 'PENDING' : u.is_active ? 'ACTIVE' : 'INACTIVE'}
                                                                                        </span>
                                                                                        {u.email || '-'}
                                                                                    </div>
                                                                                    <div className="cmc-user-status">
                                                                                        {u.last_login ? ` · Last login: ${formatLoginTime(u.last_login)}` : ' · Never logged in'}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="cmc-user-actions">
                                                                                    <button
                                                                                        className="cmc-action-label"
                                                                                        onClick={() => setEditingUser({
                                                                                            client_id: client.client_id,
                                                                                            user_id: u.user_id,
                                                                                            username: u.username,
                                                                                            full_name: u.full_name || '',
                                                                                            email: u.email || '',
                                                                                            role: u.role
                                                                                        })}
                                                                                        title="Edit User"
                                                                                    >
                                                                                        EDIT
                                                                                    </button>
                                                                                    <button
                                                                                        className="cmc-action-label"
                                                                                        onClick={() => setResettingPassword({
                                                                                            client_id: client.client_id,
                                                                                            user_id: u.user_id,
                                                                                            username: u.username,
                                                                                            email: u.email
                                                                                        })}
                                                                                        title="Send Password Reset Email"
                                                                                    >
                                                                                        RESET PW
                                                                                    </button>
                                                                                    <button
                                                                                        className={'cmc-action-label cmc-action-danger' + (u.push_enabled && u.role !== 'viewer' ? '' : ' cmc-action-muted')}
                                                                                        onClick={() => handleToggleUserPush(client.client_id, u)}
                                                                                        title={pushTitle(u)}
                                                                                        disabled={u.role === 'viewer'}
                                                                                    >
                                                                                        {u.role === 'viewer' ? 'NOTIF N/A' : (u.push_enabled ? 'NOTIF OFF' : 'NOTIF ON')}
                                                                                    </button>
                                                                                    {u.totp_enabled && (
                                                                                        <button
                                                                                            className="cmc-action-label"
                                                                                            onClick={() => handleReset2fa(client.client_id, u)}
                                                                                            title="Authenticator app is on - reset it if they lost their phone"
                                                                                        >
                                                                                            RESET 2FA
                                                                                        </button>
                                                                                    )}
                                                                                    <button
                                                                                        className="cmc-action-label"
                                                                                        onClick={() => {
                                                                                            if (!u.is_verified) {
                                                                                                showMessage('User must verify email first', 'warning');
                                                                                                return;
                                                                                            }
                                                                                            handleToggleUserStatus(client.client_id, u.user_id, u.username, u.is_active);
                                                                                        }}
                                                                                        title={!u.is_verified ? 'Must verify email first' : (u.is_active ? 'Deactivate' : 'Activate')}
                                                                                        disabled={!client.is_active || !u.is_verified}
                                                                                    >
                                                                                        {!u.is_verified ? 'PENDING' : (u.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                                                                    </button>
                                                                                    <button
                                                                                        className="cmc-action-label cmc-action-danger"
                                                                                        onClick={() => handleDeleteUser(client.client_id, u.user_id, u.username)}
                                                                                        title="Delete User"
                                                                                    >
                                                                                        DELETE
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="cmc-no-users">No users yet</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                    <div className="cm-grid-wrap">
                                        <div className="cm-grid-header">
                                            <div>CLIENT</div>
                                            <div>CONTACT EMAIL</div>
                                            <div>STATUS</div>
                                            <div className="cm-num">DEVICES</div>
                                            <div className="cm-num">USERS</div>
                                            <div>CREATED</div>
                                            <div className="cm-num">ACTIONS</div>
                                        </div>
                                        {clients.map((client, idx) => (
                                            <React.Fragment key={client.client_id}>
                                            <div className={'cm-grid-row' + (idx % 2 === 1 ? ' cm-grid-row-alt' : '')} data-tour={tourClient && client.client_id === tourClient.client_id ? 'cm-client' : undefined}>
                                                <div>
                                                    <div className="cm-client-name">{client.client_name}</div>
                                                    <div className="cm-client-id">ID {client.client_id}</div>
                                                </div>
                                                <div className="cm-email">{client.contact_email || '-'}</div>
                                                <div>
                                                    {getClientStatus(client) === 'pending' && (
                                                        <span className="cm-status-chip cm-pending">PENDING</span>
                                                    )}
                                                    {getClientStatus(client) === 'active' && (
                                                        <span className="cm-status-chip cm-active">ACTIVE</span>
                                                    )}
                                                    {getClientStatus(client) === 'inactive' && (
                                                        <span className="cm-status-chip cm-inactive">INACTIVE</span>
                                                    )}
                                                </div>
                                                <div className={'cm-num cm-mono' + (getClientDeviceCount(client.client_id) === 0 ? ' cm-dim' : '')}>
                                                    {getClientDeviceCount(client.client_id)}
                                                </div>
                                                <div className="cm-num cm-mono cm-accent">
                                                    {client.user_count || 0}
                                                </div>
                                                <div className="cm-created">
                                                    {new Date(client.created_at).toLocaleDateString()}
                                                </div>
                                                <div className="cm-row-actions" data-tour={tourClient && client.client_id === tourClient.client_id ? 'cm-client-actions' : undefined}>
                                                    <button
                                                        className="cm-action"
                                                        onClick={() => {
                                                            setEditingClient({
                                                                client_id: client.client_id,
                                                                client_name: client.client_name,
                                                                contact_email: client.contact_email || ''
                                                            });
                                                        }}
                                                        title="Edit Client"
                                                    >
                                                        EDIT
                                                    </button>
                                                    <button
                                                        className="cm-action"
                                                        onClick={() => {
                                                            setNewUser(prev => ({
                                                                ...prev,
                                                                client_id: client.client_id,
                                                                full_name: `User for ${client.client_name}`
                                                            }));
                                                            setShowNewUserForm(true);
                                                        }}
                                                        title="Add User"
                                                    >
                                                        + USER
                                                    </button>
                                                    <button
                                                        className="cm-action cm-action-muted"
                                                        onClick={() => {
                                                            if (!client.is_verified) {
                                                                showMessage('Cannot change status until client verifies email', 'warning');
                                                                return;
                                                            }
                                                            handleToggleClientStatus(client.client_id, client.is_active);
                                                        }}
                                                        title={
                                                            !client.is_verified 
                                                                ? 'Must verify email first' 
                                                                : (client.is_active ? 'Deactivate' : 'Activate')
                                                        }
                                                        disabled={!client.is_verified}
                                                    >
                                                        {!client.is_verified ? 'PENDING' : (client.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                                    </button>
                                                    <button
                                                        className="cm-action cm-action-muted"
                                                        onClick={() => {
                                                            handleToggleClientSkipStarlink(client.client_id, client.skip_starlink);
                                                        }}
                                                        title={
                                                            client.skip_starlink ? 'Show Starlink' : 'Hide Starlink'
                                                        }
                                                    >
                                                        {client.skip_starlink ? 'SHOW STARLINK' : 'HIDE STARLINK'}
                                                    </button>
                                                    <button
                                                        className="cm-action cm-action-muted"
                                                        onClick={() => handleDeleteClient(client.client_id, client.client_name)}
                                                        title="Delete Client"
                                                    >
                                                        DELETE
                                                    </button>
                                                    <button
                                                        className="cm-action"
                                                        onClick={() => toggleClientExpansion(client.client_id)}
                                                        title={expandedClient === client.client_id ? "Collapse" : "Expand"}
                                                    >
                                                        {expandedClient === client.client_id ? 'COLLAPSE ▲' : 'EXPAND ▼'}
                                                    </button>
                                                </div>
                                            </div>
                                            {expandedClient === client.client_id && (
                                                <div className="cm-grid-detail-row" data-tour="cm-client-detail">
                                                    <div className="cm-client-details">
                                                        {/* Devices Section - via linked sites, not direct assignment.
                                                            A client's devices are now whatever Peplink/Starlink
                                                            hardware sits on the site(s) linked to them in Site
                                                            Manager, so this reads from sitesByClient rather than
                                                            the old client.routers array. */}
                                                        {(() => {
                                                            const allDevices = getClientDevices(client.client_id);
                                                            const peplinkCount = allDevices.filter((d) => d.device_type === 'peplink').length;
                                                            const starlinkCount = allDevices.filter((d) => d.device_type === 'starlink').length;

                                                            if (allDevices.length === 0) {
                                                                return (
                                                                    <div className="cm-detail-section">
                                                                        <div className="cm-detail-head">
                                                                            <span>DEVICES</span>
                                                                            <span className="cm-detail-count">0</span>
                                                                        </div>
                                                                        <small className="cm-text-muted">
                                                                            No devices yet - link this client to a site with devices in Site Manager.
                                                                        </small>
                                                                    </div>
                                                                );
                                                            }

                                                            return (
                                                                <div className="cm-detail-section">
                                                                    <div className="cm-detail-head">
                                                                        <span>DEVICES</span>
                                                                        <span className="cm-detail-count">{allDevices.length} — {peplinkCount} Peplink · {starlinkCount} Starlink</span>
                                                                    </div>
                                                                    {allDevices.map((d) => (
                                                                        <div key={d.device_type + d.device_key} className="cm-detail-row">
                                                                            <span className="cm-detail-primary">{d.name || d.device_key}</span>
                                                                            <span className={'cm-kind cm-' + d.device_type}>
                                                                                {d.device_type === 'peplink' ? 'PEPLINK' : 'STARLINK'}
                                                                            </span>
                                                                            {d.source === 'roam' && <span className="cm-roam">ROAM</span>}
                                                                            <span className="cm-detail-meta">{d.site_name}</span>
                                                                        </div>
                                                                    ))}
                                                                    <small className="cm-text-muted d-block cm-mt-2">
                                                                        Manage which devices belong to this client in Site Manager.
                                                                    </small>
                                                                </div>
                                                            );
                                                        })()}
                                                        
                                                        {/* Users Section */}
                                                        {client.users && client.users.length > 0 && (
                                                            <div className="cm-detail-section">
                                                                <div className="cm-detail-head">
                                                                    <span>USERS</span>
                                                                    <span className="cm-detail-count">{client.users.length}</span>
                                                                </div>
                                                                {client.users.map(user => (
                                                                    <div key={user.user_id} className="cm-user-row">
                                                                        <span className="cm-detail-primary">{user.username}</span>
                                                                        <span className="cm-user-fullname">{user.full_name || '-'}</span>
                                                                        <span className={'cm-role-chip cm-' + user.role}>
                                                                            {user.role === 'admin' ? 'ADMIN' :
                                                                            user.role === 'operator' ? 'OPERATOR' : 'VIEWER'}
                                                                        </span>
                                                                        <div className="cm-row-actions">
                                                                            <button
                                                                                className="cm-action"
                                                                                onClick={() => {
                                                                                    setEditingUser({
                                                                                        client_id: client.client_id,
                                                                                        user_id: user.user_id,
                                                                                        username: user.username,
                                                                                        full_name: user.full_name || '',
                                                                                        email: user.email || '',
                                                                                        role: user.role
                                                                                    });
                                                                                }}
                                                                                title="Edit User"
                                                                            >
                                                                                EDIT
                                                                            </button>
                                                                            <button
                                                                                className="cm-action"
                                                                                onClick={() => {
                                                                                    setResettingPassword({
                                                                                        client_id: client.client_id,
                                                                                        user_id: user.user_id,
                                                                                        username: user.username,
                                                                                        email: user.email
                                                                                    });
                                                                                }}
                                                                                title="Send Password Reset Email"
                                                                            >
                                                                                RESET PW
                                                                            </button>
                                                                            <button
                                                                                className={'cm-action cmc-action-danger' + (user.push_enabled && user.role !== 'viewer' ? '' : ' cm-action-muted')}
                                                                                onClick={() => handleToggleUserPush(client.client_id, user)}
                                                                                title={pushTitle(user)}
                                                                                disabled={user.role === 'viewer'}
                                                                            >
                                                                                {user.role === 'viewer' ? 'NOTIF N/A' : (user.push_enabled ? 'NOTIF OFF' : 'NOTIF ON')}
                                                                            </button>
                                                                            {user.totp_enabled && (
                                                                                <button
                                                                                    className="cm-action"
                                                                                    onClick={() => handleReset2fa(client.client_id, user)}
                                                                                    title="Authenticator app is on - reset it if they lost their phone"
                                                                                >
                                                                                    RESET 2FA
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                className="cm-action cm-action-muted"
                                                                                onClick={() => {
                                                                                    if (!user.is_verified) {
                                                                                        showMessage('User must verify email first', 'warning');
                                                                                        return;
                                                                                    }
                                                                                    handleToggleUserStatus(
                                                                                        client.client_id,
                                                                                        user.user_id,
                                                                                        user.username,
                                                                                        user.is_active
                                                                                    );
                                                                                }}
                                                                                title={
                                                                                    !user.is_verified 
                                                                                        ? 'Must verify email first' 
                                                                                        : (user.is_active ? 'Deactivate' : 'Activate')
                                                                                }
                                                                                disabled={!client.is_active || !user.is_verified}
                                                                            >
                                                                                {!user.is_verified ? 'PENDING' : (user.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                                                            </button>
                                                                            <button
                                                                                className="cm-action cm-action-muted"
                                                                                onClick={() => handleDeleteUser(client.client_id, user.user_id, user.username)}
                                                                                title="Delete User"
                                                                            >
                                                                                DELETE
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    )}
                                </div>
                            </div>

                {/* Badge Management Section - hidden (SHOW_BADGE_MANAGEMENT) */}
                {SHOW_BADGE_MANAGEMENT && (
                <div className="cm-selection-section" data-tour="cm-badges">
                    <div className="cm-card badge-mgmt-card" style={{marginTop: '30px'}}>
                        <div className="cm-card-header cm-badge-mgmt-header">
                            <h5> Badge Management
                            </h5>
                            <div className="cm-badge-mgmt-header-right">
                                <span className="cm-badge-mgmt-count">
                                    {Object.keys(clientBadges).length} OF {organizations.length > 0 ? organizations.length : Object.keys(clientBadges).length} ORGS BADGED
                                </span>
                                <button
                                    className="cm-badge-mgmt-toggle"
                                    onClick={() => setShowUnbadgedOrgs((s) => !s)}
                                >
                                    {showUnbadgedOrgs ? 'HIDE UNBADGED' : 'SHOW UNBADGED ORGS'}
                                </button>
                                <button 
                                    className="cm-btn cm-btn-primary"
                                    onClick={handleOpenBadgeModal}
                                > CONFIGURE BADGE
                                </button>
                            </div>
                        </div>
                        <div className="cm-card-body p-0">
                            {(() => {
                                const badgedOrgIds = new Set(Object.keys(clientBadges));
                                const unbadgedOrgs = organizations.filter((o) => !badgedOrgIds.has(o.id));
                                const rows = [
                                    ...Object.entries(clientBadges).map(([orgId, badge]) => ({ orgId, badge, org: organizations.find((o) => o.id === orgId) })),
                                    ...(showUnbadgedOrgs ? unbadgedOrgs.map((org) => ({ orgId: org.id, badge: null, org })) : []),
                                ];

                                if (rows.length === 0) {
                                    return (
                                        <div className="cm-text-center cm-py-4">
                                            <div className="cm-empty-state">
                                                <p className="cm-text-muted mb-0">No badges configured yet</p>
                                                <small className="cm-text-muted">Click "Configure badge" to create custom badges for organizations</small>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="cm-badge-mgmt-table-wrap">
                                        <table className="cm-badge-mgmt-table">
                                            <thead>
                                                <tr>
                                                    <th>Badge</th>
                                                    <th>Organization</th>
                                                    <th>Org code</th>
                                                    <th>Colours</th>
                                                    <th>Contrast</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map(({ orgId, badge, org }) => {
                                                    const ratio = badge ? contrastRatio(badge.badge_color, badge.badge_font_color) : null;
                                                    const passes = ratio !== null && ratio >= CONTRAST_AA_THRESHOLD;
                                                    return (
                                                        <tr key={orgId} className={!badge ? 'cm-badge-row-unbadged' : ''}>
                                                            <td>
                                                                {badge ? (
                                                                    <span
                                                                        className="cm-badge-preview"
                                                                        style={{ backgroundColor: badge.badge_color, color: badge.badge_font_color }}
                                                                    >
                                                                        {badge.badge_name}
                                                                    </span>
                                                                ) : (
                                                                    <span className="cm-badge-mgmt-none">— none —</span>
                                                                )}
                                                            </td>
                                                            <td>{org?.display_name || org?.name || orgId}</td>
                                                            <td>{org?.org_code ? <code className="cm-small">{org.org_code}</code> : <span className="cm-text-muted">—</span>}</td>
                                                            <td>
                                                                {badge ? (
                                                                    <div className="cm-badge-mgmt-swatches">
                                                                        <span className="cm-badge-mgmt-swatch" style={{ background: badge.badge_color }} title={`Background ${badge.badge_color}`} />
                                                                        <span className="cm-badge-mgmt-swatch" style={{ background: badge.badge_font_color }} title={`Text ${badge.badge_font_color}`} />
                                                                    </div>
                                                                ) : <span className="cm-text-muted">—</span>}
                                                            </td>
                                                            <td>
                                                                {badge && ratio !== null ? (
                                                                    <div className="cm-badge-mgmt-contrast">
                                                                        <span className={'cm-contrast-chip ' + (passes ? 'cm-pass' : 'cm-fail')}>
                                                                            {ratio.toFixed(1)}:1 {passes ? 'PASS' : 'FAIL'}
                                                                        </span>
                                                                        {!passes && (
                                                                            <button
                                                                                className="cm-contrast-fix-btn"
                                                                                onClick={async () => {
                                                                                    const fixedFont = bestTextColorFor(badge.badge_color);
                                                                                    setLoadingBadges(true);
                                                                                    try {
                                                                                        await apiFetchJSON('/api/admin/client-badges', {
                                                                                            method: 'POST',
                                                                                            body: JSON.stringify({
                                                                                                organization_id: orgId,
                                                                                                badge_name: badge.badge_name,
                                                                                                badge_color: badge.badge_color,
                                                                                                badge_font_color: fixedFont,
                                                                                            }),
                                                                                        });
                                                                                        await fetchBadges();
                                                                                        showMessage('Text color updated for contrast', 'success');
                                                                                    } catch (e) {
                                                                                        showMessage(e.message || 'Failed to fix contrast', 'error');
                                                                                    } finally {
                                                                                        setLoadingBadges(false);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                FIX TEXT
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ) : <span className="cm-text-muted">—</span>}
                                                            </td>
                                                            <td>
                                                                {badge ? (
                                                                    <div className="cm-badge-config-actions">
                                                                        <button 
                                                                            className="cm-row-action-label"
                                                                            onClick={() => handleEditBadge(orgId)}
                                                                            title="Edit Badge"
                                                                        >
                                                                            EDIT
                                                                        </button>
                                                                        <button 
                                                                            className="cm-row-action-label cm-row-action-muted"
                                                                            onClick={() => handleDeleteBadge(orgId, badge.badge_name)}
                                                                            title="Delete Badge"
                                                                        >
                                                                            DELETE
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        className="cm-btn cm-btn-xs cm-btn-outline-primary"
                                                                        onClick={() => {
                                                                            setBadgeFormData({ organization_id: orgId, badge_name: '', badge_color: '#3b82f6', badge_font_color: '#ffffff' });
                                                                            setEditingBadgeOrg(null);
                                                                            setShowBadgeModal(true);
                                                                        }}
                                                                    >
                                                                        CONFIGURE
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* Modals */}
            {showNewUserForm && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                ADD USER
                                {newUser.client_id && (() => {
                                    const c = clients.find((c) => c.client_id === newUser.client_id);
                                    return c ? (
                                        <>
                                            <span className="cm-modal-title-sep">/</span>
                                            <span className="cm-modal-title-entity">{c.client_name}</span>
                                        </>
                                    ) : null;
                                })()}
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setShowNewUserForm(false)}
                            ></button>
                        </div>
                        <form onSubmit={handleAddUser}>
                            <div className="cm-client-manager-modal-body">
                                <div className="cm-form-group">
                                    <label className="cm-form-label">Client *</label>
                                    <select
                                        className="cm-form-control"
                                        value={newUser.client_id}
                                        onChange={(e) => handleClientSelectForUser(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Select Client --</option>
                                        {clients
                                            .filter(client => client.is_active)
                                            .map(client => (
                                                <option key={client.client_id} value={client.client_id}>
                                                    {client.client_name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                
                                <div className="cm-row cm-mt-3">
                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Username *</label>
                                            <div className="cm-input-group">
                                                <span className="input-group-text cm-username-prefix">
                                                    {newUser.client_id ? 
                                                        getDefaultUsernamePrefix(
                                                            clients.find(c => c.client_id === newUser.client_id)?.client_name
                                                        ) : 
                                                        ''
                                                    }
                                                </span>
                                                <input
                                                    type="text"
                                                    className="cm-form-control"
                                                    value={newUser.usernameSuffix || ''}
                                                    style={userFieldErrors.username ? invalidInputStyle : undefined}
                                                    aria-invalid={!!userFieldErrors.username}
                                                    onChange={(e) => {
                                                        clearUserFieldError('username');
                                                        const prefix = newUser.client_id ? 
                                                            getDefaultUsernamePrefix(
                                                                clients.find(c => c.client_id === newUser.client_id)?.client_name
                                                            ) : 
                                                            '';
                                                        setNewUser({
                                                            ...newUser,
                                                            usernameSuffix: e.target.value,
                                                            username: prefix + e.target.value
                                                        });
                                                    }}
                                                    required
                                                    placeholder="username"
                                                />
                                            </div>
                                            <FieldError message={userFieldErrors.username} />
                                            <div className="cm-form-text">
                                                Full username: <code>{newUser.username || ''}</code>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Email *</label>
                                            <input
                                                type="email"
                                                className="cm-form-control"
                                                value={newUser.email}
                                                style={userFieldErrors.email ? invalidInputStyle : undefined}
                                                aria-invalid={!!userFieldErrors.email}
                                                onChange={(e) => {
                                                    clearUserFieldError('email');
                                                    setNewUser({...newUser, email: e.target.value});
                                                }}
                                                required
                                                placeholder="user@example.com"
                                            />
                                            <FieldError message={userFieldErrors.email} />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="cm-row cm-mt-2">
                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Full Name</label>
                                            <input
                                                type="text"
                                                className="cm-form-control"
                                                value={newUser.full_name}
                                                onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                                                placeholder="John Doe"
                                            />
                                        </div>
                                    </div>
                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Role</label>
                                            <select
                                                className="cm-form-control"
                                                value={newUser.role}
                                                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                                            >
                                                <option value="viewer">Viewer (Read-only)</option>
                                                <option value="operator">Operator (Limited actions)</option>
                                                <option value="admin">Admin (Full access within client)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="cm-alert cm-alert-info cm-mt-3"> The user will receive an email to set their own password.
                                </div>
                            </div>
                            <div className="cm-modal-footer">
                                <button 
                                    type="button" 
                                    className="cm-btn cm-btn-secondary" 
                                    onClick={() => setShowNewUserForm(false)}
                                >
                                    CANCEL
                                </button>
                                <button 
                                    type="submit" 
                                    className="cm-btn cm-btn-primary"
                                    disabled={!newUser.client_id || !newUser.username || !newUser.email}
                                >
                                    CREATE USER
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Client Modal */}
            {editingClient && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                EDIT CLIENT
                                <span className="cm-modal-title-sep">/</span>
                                <span className="cm-modal-title-entity">{editingClient.client_name}</span>
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setEditingClient(null)}
                            ></button>
                        </div>
                        <div className="cm-client-manager-modal-body">
                            <div className="cm-form-group cm-mb-3">
                                <label className="cm-form-label">Client Name *</label>
                                <input
                                    type="text"
                                    className="cm-form-control"
                                    value={editingClient.client_name}
                                    onChange={(e) => setEditingClient({
                                        ...editingClient,
                                        client_name: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="cm-form-group">
                                <label className="cm-form-label">Contact Email *</label>
                                <input
                                    type="email"
                                    className="cm-form-control"
                                    value={editingClient.contact_email}
                                    onChange={(e) => setEditingClient({
                                        ...editingClient,
                                        contact_email: e.target.value
                                    })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="cm-modal-footer">
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-secondary" 
                                onClick={() => setEditingClient(null)}
                            >
                                CANCEL
                            </button>
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-primary"
                                onClick={handleEditClient}
                            >
                                SAVE CHANGES
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                EDIT USER
                                <span className="cm-modal-title-sep">/</span>
                                <span className="cm-modal-title-entity">{editingUser.username}</span>
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setEditingUser(null)}
                            ></button>
                        </div>
                        <div className="cm-client-manager-modal-body">
                            <div className="cm-row cm-mt-3">
                                <div className="cm-col-md-6">
                                    <div className="cm-form-group">
                                        <label className="cm-form-label">Full Name</label>
                                        <input
                                            type="text"
                                            className="cm-form-control"
                                            value={editingUser.full_name}
                                            onChange={(e) => setEditingUser({
                                                ...editingUser,
                                                full_name: e.target.value
                                            })}
                                            placeholder="John Doe"
                                        />
                                    </div>
                                </div>
                                <div className="cm-col-md-6">
                                    <div className="cm-form-group">
                                        <label className="cm-form-label">Email</label>
                                        <input
                                            type="email"
                                            className="cm-form-control"
                                            value={editingUser.email}
                                            style={userFieldErrors.email ? invalidInputStyle : undefined}
                                            aria-invalid={!!userFieldErrors.email}
                                            onChange={(e) => {
                                                clearUserFieldError('email');
                                                setEditingUser({
                                                    ...editingUser,
                                                    email: e.target.value
                                                });
                                            }}
                                            placeholder="john@example.com"
                                        />
                                        <FieldError message={userFieldErrors.email} />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="cm-form-group cm-mt-3">
                                <label className="cm-form-label">Role</label>
                                <select
                                    className="cm-form-control"
                                    value={editingUser.role}
                                    onChange={(e) => setEditingUser({
                                        ...editingUser,
                                        role: e.target.value
                                    })}
                                >
                                    <option value="viewer">Viewer (Read-only)</option>
                                    <option value="operator">Operator (Limited actions)</option>
                                    <option value="admin">Admin (Full access within client)</option>
                                </select>
                            </div>
                        </div>
                        <div className="cm-modal-footer">
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-secondary" 
                                onClick={() => setEditingUser(null)}
                            >
                                CANCEL
                            </button>
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-primary"
                                onClick={handleEditUser}
                            >
                                SAVE CHANGES
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Send Reset Email Modal*/}
            {resettingPassword && resettingPassword.email && !resettingPassword.new_password && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                RESET PASSWORD
                                <span className="cm-modal-title-sep">/</span>
                                <span className="cm-modal-title-entity">{resettingPassword.username}</span>
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setResettingPassword(null)}
                            ></button>
                        </div>
                        <div className="cm-client-manager-modal-body">
                            <div className="cm-alert cm-alert-info"> You are sending a password reset email to :
                            </div>
                            
                            <div className="reset-email-info cm-mb-3">
                                <div><strong>User :</strong> {resettingPassword.username}</div>
                                <div><strong>Email :</strong> {resettingPassword.email}</div>
                            </div>
                            
                            <div className="cm-alert cm-alert-warning"> The user will receive an email with a link to set their own password.
                            </div>
                        </div>
                        <div className="cm-modal-footer">
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-secondary" 
                                onClick={() => setResettingPassword(null)}
                            >
                                CANCEL
                            </button>
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-primary"
                                onClick={handleSendResetEmail}
                                disabled={sendingReset}
                            >
                                {sendingReset ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin cm-me-2"></i>
                                        Sending...
                                    </>
                                ) : (
                                    <> Send Password Reset Email
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Badge Management Modal */}
            {SHOW_BADGE_MANAGEMENT && showBadgeModal && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content cm-badge-modal">
                        <div className="cm-modal-header">
                            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="#88F4EB" strokeWidth="1.7">
                                <path d="M10.5 2.5H16a1.5 1.5 0 011.5 1.5v5.5L9 18l-7-7 8.5-8.5z"></path>
                                <circle cx="13.6" cy="6.4" r="1.2" fill="#88F4EB" stroke="none"></circle>
                            </svg>
                            <h5 className="cm-modal-title">
                                {editingBadgeOrg ? 'EDIT BADGE CONFIGURATION' : 'CREATE BADGE CONFIGURATION'}
                                {editingBadgeOrg && (() => {
                                    const org = organizations.find((o) => o.id === editingBadgeOrg);
                                    return (
                                        <>
                                            <span className="cm-modal-title-sep">/</span>
                                            <span className="cm-modal-title-entity">{org?.display_name || org?.name || editingBadgeOrg}</span>
                                        </>
                                    );
                                })()}
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => {
                                    setShowBadgeModal(false);
                                    setEditingBadgeOrg(null);
                                }}
                            ></button>
                        </div>

                        {/* Info line - matches 5b/5c exactly, differs by create/edit */}
                        <div className="cm-badge-modal-info">
                            {editingBadgeOrg ? (() => {
                                const org = organizations.find((o) => o.id === editingBadgeOrg);
                                const deviceCount = org?.device_count;
                                return `Applies to all${deviceCount != null ? ` ${deviceCount}` : ''} device${deviceCount === 1 ? '' : 's'} ${org?.display_name || org?.name || 'this organization'} owns. Changes take effect on next poll — no device restart.`;
                            })() : (
                                'One badge per organization. It appears next to every device that org owns, so keep it short enough to read at row size.'
                            )}
                        </div>

                        <div className="cm-client-manager-modal-body">
                            <form onSubmit={(e) => { e.preventDefault(); handleSaveBadge(); }}>
                                <div className="cm-badge-modal-row-2col">
                                    {/* Organization Selection - locked to a static display once
                                        editing, matching 5c: an org can't be moved to a different
                                        badge without deleting and recreating it. */}
                                    <div className="cm-badge-modal-field">
                                        <label className="cm-form-label">ORGANIZATION *</label>
                                        {editingBadgeOrg ? (
                                            <>
                                                <div className="cm-badge-org-locked">
                                                    <span>
                                                        {(() => {
                                                            const org = organizations.find((o) => o.id === editingBadgeOrg);
                                                            const name = org?.display_name || org?.name || editingBadgeOrg;
                                                            return org?.org_code ? `${name} (${org.org_code})` : name;
                                                        })()}
                                                    </span>
                                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="1.5" style={{ marginLeft: 'auto' }}>
                                                        <rect x="3" y="7" width="10" height="7"></rect>
                                                        <path d="M5.5 7V5a2.5 2.5 0 015 0v2"></path>
                                                    </svg>
                                                </div>
                                                <div className="cm-badge-modal-hint">
                                                    LOCKED WHILE EDITING — DELETE AND RECREATE TO MOVE IT
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <select
                                                    className="cm-form-control cm-badge-org-select"
                                                    value={badgeFormData.organization_id}
                                                    onChange={(e) => setBadgeFormData({
                                                        ...badgeFormData,
                                                        organization_id: e.target.value
                                                    })}
                                                    required
                                                >
                                                    <option value="">SELECT ORGANIZATION…</option>
                                                    {organizations.map(org => (
                                                        <option 
                                                            key={org.id} 
                                                            value={org.id}
                                                            disabled={org.hasBadge}
                                                        >
                                                            {org.display_name}
                                                            {org.hasBadge && ' (Badge already configured)'}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="cm-badge-modal-hint">
                                                    {organizations.filter((o) => !o.hasBadge).length} ORGS HAVE NO BADGE YET
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Badge Name - live-validated: uppercase letters, digits,
                                        underscore only, max 6 chars, matching 5b exactly. */}
                                    <div className="cm-badge-modal-field">
                                        <label className="cm-form-label cm-badge-name-label">
                                            <span>BADGE NAME *</span>
                                            <span className="cm-badge-name-counter">{badgeFormData.badge_name.length} / 6</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="cm-form-control cm-badge-name-input"
                                            value={badgeFormData.badge_name}
                                            onChange={(e) => setBadgeFormData({
                                                ...badgeFormData,
                                                badge_name: sanitizeBadgeName(e.target.value)
                                            })}
                                            placeholder="MAX 6 CHARS"
                                            required
                                            maxLength="6"
                                        />
                                        <div className="cm-badge-modal-hint">
                                            {badgeFormData.badge_name.length >= 6 ? 'AT LIMIT' : 'UPPERCASE · A–Z 0–9 _ ONLY'}
                                        </div>
                                    </div>
                                </div>

                                {/* Color Palette */}
                                <div className="cm-badge-modal-palette-head">
                                    <span className="cm-form-label" style={{ marginBottom: 0 }}>QUICK PALETTE</span>
                                    <span className="cm-badge-modal-palette-note">
                                        {(() => {
                                            const ratio = contrastRatio(badgeFormData.badge_color, badgeFormData.badge_font_color);
                                            const onPalette = colorPalettes.some((p) => p.bg === badgeFormData.badge_color);
                                            if (onPalette && ratio !== null && ratio < CONTRAST_AA_THRESHOLD) {
                                                return <span style={{ color: 'var(--color-accent)' }}>CURRENT SWATCH FAILS — PICK ANOTHER OR DARKEN THE TEXT</span>;
                                            }
                                            return 'TEXT COLOUR IS SET WITH THE SWATCH';
                                        })()}
                                    </span>
                                </div>
                                <div className="cm-color-palette-grid">
                                    {colorPalettes.map((palette, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className={'cm-color-palette-btn' + (badgeFormData.badge_color === palette.bg ? ' cm-selected' : '')}
                                            style={{
                                                backgroundColor: palette.bg,
                                                color: palette.font,
                                            }}
                                            onClick={() => setBadgeFormData({
                                                ...badgeFormData,
                                                badge_color: palette.bg,
                                                badge_font_color: palette.font
                                            })}
                                            title={palette.name}
                                        >
                                            Aa
                                        </button>
                                    ))}
                                </div>
                                
                                {/* Color Selection - solid swatch + hex field, matching 5b/5c exactly */}
                                <div className="cm-badge-modal-row-2col cm-badge-modal-color-row">
                                    <div className="cm-badge-modal-field">
                                        <label className="cm-form-label">CUSTOM BADGE COLOUR</label>
                                        <div className="cm-badge-color-input-row">
                                            <label className="cm-badge-color-swatch" style={{ background: badgeFormData.badge_color }}>
                                                <input
                                                    type="color"
                                                    value={badgeFormData.badge_color}
                                                    onChange={(e) => setBadgeFormData({ ...badgeFormData, badge_color: e.target.value })}
                                                />
                                            </label>
                                            <input
                                                type="text"
                                                className="cm-form-control cm-badge-hex-input"
                                                value={badgeFormData.badge_color}
                                                onChange={(e) => setBadgeFormData({
                                                    ...badgeFormData,
                                                    badge_color: e.target.value
                                                })}
                                                placeholder="#RRGGBB"
                                                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                            />
                                        </div>
                                    </div>
                                    <div className="cm-badge-modal-field">
                                        <label className="cm-form-label">CUSTOM TEXT COLOUR</label>
                                        <div className="cm-badge-color-input-row">
                                            <label className="cm-badge-color-swatch" style={{ background: badgeFormData.badge_font_color }}>
                                                <input
                                                    type="color"
                                                    value={badgeFormData.badge_font_color}
                                                    onChange={(e) => setBadgeFormData({ ...badgeFormData, badge_font_color: e.target.value })}
                                                />
                                            </label>
                                            <input
                                                type="text"
                                                className="cm-form-control cm-badge-hex-input"
                                                value={badgeFormData.badge_font_color}
                                                onChange={(e) => setBadgeFormData({
                                                    ...badgeFormData,
                                                    badge_font_color: e.target.value
                                                })}
                                                placeholder="#RRGGBB"
                                                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Live contrast check - real WCAG 2.1 math. The message names a
                                    specific alternative (whichever of black/white actually clears
                                    4.5:1) and its resulting ratio, rather than a generic warning. */}
                                {(() => {
                                    const ratio = contrastRatio(badgeFormData.badge_color, badgeFormData.badge_font_color);
                                    if (ratio === null) return null;
                                    const passes = ratio >= CONTRAST_AA_THRESHOLD;
                                    const suggestion = bestTextColorFor(badgeFormData.badge_color);
                                    const suggestionRatio = contrastRatio(badgeFormData.badge_color, suggestion);
                                    return (
                                        <div className={'cm-contrast-check-row ' + (passes ? 'cm-pass' : 'cm-fail')}>
                                            <span className="cm-contrast-chip">
                                                CONTRAST {ratio.toFixed(1)}:1
                                            </span>
                                            <span className="cm-contrast-check-label">
                                                {passes ? (
                                                    'Meets WCAG AA (4.5:1) for normal text.'
                                                ) : (
                                                    <>
                                                        Short of 4.5:1.{' '}
                                                        <span style={{ color: 'var(--color-accent)' }}>Use {suggestion}</span>
                                                        {' '}for {suggestionRatio ? suggestionRatio.toFixed(1) : '?'}:1 — this colour is too mid-toned for {suggestion === '#FFFFFF' ? 'white' : 'near-black'} text.
                                                    </>
                                                )}
                                            </span>
                                            {!passes && (
                                                <button
                                                    type="button"
                                                    className="cm-contrast-fix-btn"
                                                    onClick={() => setBadgeFormData({
                                                        ...badgeFormData,
                                                        badge_font_color: suggestion,
                                                    })}
                                                >
                                                    APPLY FIX
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()}
                                
                                {/* Preview in context - illustrative only, not tied to real device
                                    data (the org<->device linkage needed for a real preview isn't
                                    confirmed to exist - see earlier note on this). */}
                                <div className="cm-badge-modal-preview-head">PREVIEW IN CONTEXT</div>
                                <div className="cm-badge-modal-preview-table">
                                    <div className="cm-badge-modal-preview-row cm-header">
                                        <div>BADGE</div><div>DEVICE</div><div>SERIAL</div>
                                    </div>
                                    {['MAX_BR1_9020-1088', 'Balance_SDX_7277'].map((name, i) => (
                                        <div className="cm-badge-modal-preview-row" key={i}>
                                            <div>
                                                <span className="cm-badge-preview" style={{ backgroundColor: badgeFormData.badge_color, color: badgeFormData.badge_font_color }}>
                                                    {badgeFormData.badge_name || 'BADGE'}
                                                </span>
                                            </div>
                                            <div className="cm-badge-modal-preview-device">{name}</div>
                                            <div className="cm-badge-modal-preview-serial">example only</div>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="cm-modal-footer cm-badge-modal-footer">
                                    <span className="cm-badge-modal-footer-note">
                                        ORGANIZATION AND NAME REQUIRED · CONTRAST MUST CLEAR 4.5:1
                                    </span>
                                    <button 
                                        type="button" 
                                        className="cm-btn cm-btn-secondary" 
                                        onClick={() => {
                                            setShowBadgeModal(false);
                                            setEditingBadgeOrg(null);
                                        }}
                                    >
                                        CANCEL
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="cm-btn cm-btn-primary"
                                        disabled={
                                            loadingBadges ||
                                            !badgeFormData.organization_id ||
                                            !badgeFormData.badge_name ||
                                            !BADGE_NAME_PATTERN.test(badgeFormData.badge_name) ||
                                            (contrastRatio(badgeFormData.badge_color, badgeFormData.badge_font_color) || 0) < CONTRAST_AA_THRESHOLD
                                        }
                                    >
                                        {loadingBadges ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin cm-me-2"></i>
                                                Saving...
                                            </>
                                        ) : (
                                            'Save badge configuration'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default ClientManager;
